'use client';
import React, { useState, useRef, useEffect } from 'react';
import { X, Phone, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

interface OTPModalProps {
  phone: string;
  onVerified: () => void;
  onClose: () => void;
}

export default function OTPModal({ phone, onVerified, onClose }: OTPModalProps) {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (countdown > 0) {
      const t = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(t);
    } else {
      setCanResend(true);
    }
  }, [countdown]);

  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    setError('');
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newOtp = [...otp];
    pasted.split('').forEach((char, i) => { if (i < 6) newOtp[i] = char; });
    setOtp(newOtp);
    inputRefs.current[Math.min(pasted.length, 5)]?.focus();
  };

  const handleVerify = async () => {
    const code = otp.join('');
    if (code.length < 6) {
      setError('Please enter all 6 digits');
      return;
    }
    setLoading(true);
    // Backend integration point: POST /api/auth/otp/verify
    await new Promise((r) => setTimeout(r, 1200));
    // Mock: accept 123456 as valid
    if (code !== '123456') {
      setError('Invalid OTP. Please try again.');
      setLoading(false);
      return;
    }
    toast.success('Phone number verified successfully!');
    onVerified();
  };

  const handleResend = async () => {
    // Backend integration point: POST /api/auth/otp/resend
    setCountdown(60);
    setCanResend(false);
    setOtp(['', '', '', '', '', '']);
    toast.info('New OTP sent to your phone');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-foreground/30 backdrop-blur-sm">
      <div className="bg-card w-full max-w-sm rounded-2xl shadow-card-lg border border-border animate-slide-up">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Phone size={18} className="text-primary" />
            </div>
            <div>
              <h3 className="font-bold text-base text-foreground">Verify Phone Number</h3>
              <p className="text-xs text-muted-foreground">OTP sent to {phone}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted transition-all">
            <X size={18} className="text-muted-foreground" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <p className="text-sm text-muted-foreground text-center">
            Enter the 6-digit code sent to your phone. Demo code: <strong className="text-foreground">123456</strong>
          </p>

          <div className="flex gap-2 justify-center" onPaste={handlePaste}>
            {otp.map((digit, i) => (
              <input
                key={`otp-digit-${i}`}
                ref={(el) => { inputRefs.current[i] = el; }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                className={`w-11 h-13 text-center text-xl font-bold border-2 rounded-xl transition-all focus:outline-none focus:ring-2 focus:ring-primary/30 ${
                  digit ? 'border-primary bg-secondary' : 'border-border bg-card'
                } ${error ? 'border-red-400' : ''}`}
                style={{ height: '52px' }}
              />
            ))}
          </div>

          {error && (
            <p className="text-xs text-red-600 text-center">{error}</p>
          )}

          <button
            onClick={handleVerify}
            disabled={loading}
            className="w-full py-3 bg-primary text-white text-sm font-bold rounded-lg hover:bg-primary/90 active:scale-[0.98] transition-all disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {loading ? <><Loader2 size={16} className="animate-spin" /> Verifying...</> : 'Verify & Create Account'}
          </button>

          <div className="text-center">
            {canResend ? (
              <button
                onClick={handleResend}
                className="flex items-center gap-1.5 mx-auto text-sm text-primary font-semibold hover:underline"
              >
                <RefreshCw size={14} />
                Resend OTP
              </button>
            ) : (
              <p className="text-sm text-muted-foreground">
                Resend OTP in <span className="font-semibold text-foreground font-tabular">{countdown}s</span>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}